if (!customElements.get('bp-model-gallery')) {
  customElements.define('bp-model-gallery', class extends HTMLElement {
    connectedCallback() {
      try {
        this.initializeGallery();
      } catch (error) {
        this.restoreFallback();
        console.error('BOPMAN gallery initialization failed', error);
      }
    }

    get picker() {
      return this.host?.querySelector('variant-selects');
    }

    initializeGallery() {
      if (this.ready) return;
      const data = JSON.parse(this.querySelector('[data-model-data]').textContent);
      this.host = this.closest('product-info');
      this.description = this.host?.querySelector('.product__description');
      if (!this.picker || !this.description) return;
      const normalize = (value) => value.replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').trim();
      this.variants = data.variants;
      this.models = this.variants.map((variant) => {
        const code = variant.sku || variant.title;
        const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return { variant, code, pattern: new RegExp(`(^|[^A-Z0-9-])${escaped}(?=$|[^A-Z0-9-])`, 'i'), rows: [...this.description.querySelectorAll('tbody tr')].filter((row) => normalize(row.cells[0]?.textContent || '') === code) };
      });
      if (this.picker.dataset.productHandle === 'teak-long-plank-flooring-qh-2501-qh-2502') {
        this.models.forEach((model) => {
          model.records = [];
          let specific = false;
          this.description.querySelectorAll('tbody tr').forEach((row) => {
            if (row.cells.length !== 2 || row.cells[1].querySelector('a')) return;
            const label = row.cells[0].textContent.trim();
            const value = normalize(row.cells[1].textContent);
            const owners = this.models.filter((entry) => entry.pattern.test(value));
            if (!owners.length) model.records.push({ label, cell: row.cells[1] });
            else {
              const marker = new RegExp(`${model.code}\\s*[:：]`, 'i');
              const match = marker.exec(value);
              if (!match) return;
              const rest = value.slice(match.index + match[0].length);
              const stops = this.models.filter((entry) => entry !== model).map((entry) => rest.search(new RegExp(`${entry.code}\\s*[:：]`, 'i'))).filter((index) => index >= 0);
              const text = rest.slice(0, stops.length ? Math.min(...stops) : undefined).trim();
              if (text) { model.records.push({ label, text }); specific = true; }
            }
          });
          if (!specific) model.records = [];
        });
      }
      if (this.models.some((model) => !model.rows.length && !model.records?.length)) return;
      this.media = data.media.map((media) => {
        const owners = this.models.filter((model) => model.pattern.test(`${media.alt || ''} ${media.src}`));
        return { ...media, model: owners.length === 1 ? owners[0] : null };
      });
      if (!this.media.length || this.media.some((media) => media.type !== 'image' || !media.model) || this.models.some((model) => !this.media.some((media) => media.model === model))) return;
      this.abort = new AbortController();
      const on = (node, type, callback, options = {}) => node.addEventListener(type, callback, { ...options, signal: this.abort.signal });
      this.track = this.querySelector('.bp-mg-track');
      this.thumbnails = this.querySelector('.bp-mg-thumbnails');
      this.dialog = this.querySelector('dialog');
      this.zoom = this.querySelector('.bp-mg-zoom img');
      this.track.replaceChildren();
      this.thumbnails.replaceChildren();
      const openLabel = this.querySelector('[data-open-label]').content.textContent;
      const createImage = (media, thumb = false) => {
        const image = document.createElement('img');
        image.src = thumb ? media.thumb : media.src;
        image.alt = media.alt || media.model.code;
        image.width = thumb ? 180 : media.width;
        image.height = thumb ? 135 : media.height;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        return image;
      };
      this.media.forEach((media, index) => {
        const slide = document.createElement('button');
        slide.type = 'button';
        slide.className = 'bp-mg-slide';
        slide.dataset.mediaId = media.id;
        slide.setAttribute('aria-label', `${media.model.code} · ${openLabel.replace('[index]', index + 1)}`);
        slide.append(createImage(media));
        on(slide, 'click', (event) => {
          if (this.dragged && event.detail > 0) { event.preventDefault(); return; }
          this.go(index);
          this.dialog.showModal();
        });
        this.track.append(slide);
        const thumbnail = document.createElement('button');
        thumbnail.type = 'button';
        thumbnail.setAttribute('aria-label', `${media.model.code} · ${index + 1} / ${this.media.length}`);
        const code = document.createElement('bdi');
        code.textContent = media.model.code;
        thumbnail.append(createImage(media, true), code);
        on(thumbnail, 'click', () => this.go(index));
        this.thumbnails.append(thumbnail);
      });
      this.summary = document.createElement('section');
      this.summary.className = 'bp-mg-summary';
      this.summary.setAttribute('aria-live', 'polite');
      this.summary.setAttribute('aria-atomic', 'true');
      this.picker.after(this.summary);
      on(this.host, 'change', (event) => {
        if (!this.picker?.contains(event.target)) return;
        const value = event.target.value;
        const model = this.models.find((model) => model.variant.options[0] === value);
        if (!model) return;
        event.stopImmediatePropagation();
        if (this.media[this.index]?.model === model) return;
        this.go(this.media.findIndex((media) => media.model === model));
      }, { capture: true });
      this.querySelectorAll('[data-mg-prev]').forEach((button) => on(button, 'click', () => this.go(this.index - 1)));
      this.querySelectorAll('[data-mg-next]').forEach((button) => on(button, 'click', () => this.go(this.index + 1)));
      on(this.querySelector('.bp-mg-close'), 'click', () => this.dialog.close());
      on(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.media.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const width = this.track.clientWidth;
          if (width) this.select(Math.max(0, Math.min(this.media.length - 1, Math.round(this.track.scrollLeft / width))));
        });
      }, { passive: true });
      on(this.track, 'pointerdown', (event) => {
        this.dragged = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.pointer = { x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft, id: event.pointerId };
      });
      on(this.track, 'pointermove', (event) => {
        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.x;
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(event.clientY - this.pointer.y)) {
          this.dragged = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.style.scrollSnapType = 'none';
          this.track.scrollLeft = this.pointer.scroll - dx;
          event.preventDefault();
        }
      });
      const endDrag = () => {
        if (!this.pointer) return;
        this.pointer = null;
        this.track.style.scrollSnapType = '';
        if (this.dragged) this.go(Math.round(this.track.scrollLeft / this.track.clientWidth));
      };
      on(window, 'pointerup', endDrag);
      on(this.track, 'pointercancel', endDrag);
      let touch;
      on(this.querySelector('.bp-mg-zoom'), 'touchstart', (event) => {
        touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      on(this.querySelector('.bp-mg-zoom'), 'touchend', (event) => {
        if (!touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touch.x;
        const dy = event.changedTouches[0].clientY - touch.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1));
        touch = null;
      }, { passive: true });
      on(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const index = this.media.findIndex((media) => String(media.model.variant.id) === id);
        if (index >= 0) this.go(index);
      });
      const currentValue = this.picker.querySelector('input:checked')?.value || this.picker.querySelector('select')?.value;
      const currentVariant = this.variants.find((variant) => variant.options[0] === currentValue);
      const initialId = String(currentVariant?.id || this.dataset.selected);
      const initial = this.media.findIndex((media) => String(media.model.variant.id) === initialId);
      this.nativeGallery = this.host.querySelector('media-gallery');
      this.hidden = false;
      this.go(Math.max(0, initial), initialId !== this.dataset.selected);
      this.ready = true;
      this.host.setAttribute('data-bp-model-gallery-ready', '');
      this.host.abortController?.abort();
      this.host.pendingRequestUrl = null;
      if (this.nativeGallery) this.nativeGallery.hidden = true;
      this.resize = new ResizeObserver(() => this.go(this.index, false));
      this.resize.observe(this.track);
      this.pickerObserver = new MutationObserver((records) => {
        const changed = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => node.matches?.('variant-selects') || node.querySelector?.('variant-selects')));
        if (this.ready && changed && this.picker) {
          this.picker.after(this.summary);
          this.select(this.index, false);
        }
      });
      this.pickerObserver.observe(this.host, { childList: true, subtree: true });
    }

    go(index, updateUrl = true) {
      index = Math.max(0, Math.min(this.media.length - 1, index));
      this.select(index, updateUrl);
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'instant' });
    }

    select(index, updateUrl = true) {
      const media = this.media[index];
      if (!media) return;
      const modelChanged = this.media[this.index]?.model !== media.model;
      this.index = index;
      this.dataset.activeMedia = media.id;
      this.dataset.activeVariant = media.model.variant.id;
      this.track.children[index].querySelector('img').loading = 'eager';
      this.track.querySelectorAll('.bp-mg-slide').forEach((slide, i) => { slide.tabIndex = i === index ? 0 : -1; slide.setAttribute('aria-hidden', String(i !== index)); });
      [...this.thumbnails.children].forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
      const thumbnail = this.thumbnails.children[index];
      const rect = thumbnail.getBoundingClientRect();
      const container = this.thumbnails.getBoundingClientRect();
      if (rect.left < container.left || rect.right > container.right) this.thumbnails.scrollTo({ left: thumbnail.offsetLeft - this.thumbnails.offsetLeft, behavior: 'instant' });
      this.querySelectorAll('[data-mg-status]').forEach((status) => { status.textContent = `${media.model.code} · ${index + 1} / ${this.media.length}`; });
      this.querySelectorAll('[data-mg-prev]').forEach((button) => { button.disabled = index === 0; });
      this.querySelectorAll('[data-mg-next]').forEach((button) => { button.disabled = index === this.media.length - 1; });
      this.picker.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = input.value === media.model.variant.options[0]; });
      this.picker.querySelectorAll('select').forEach((select) => { select.value = media.model.variant.options[0]; });
      this.picker.querySelector('[data-selected-variant]').textContent = JSON.stringify(media.model.variant);
      if (modelChanged || !this.summary.children.length) this.updateSummary(media.model);
      this.zoom.src = media.src;
      this.zoom.alt = media.alt || media.model.code;
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', media.model.variant.id);
        history.replaceState(history.state, '', url);
      }
    }

    updateSummary(model) {
      this.summary.replaceChildren();
      const heading = document.createElement('h2');
      const code = document.createElement('bdi');
      code.textContent = model.code;
      heading.append(code);
      this.summary.append(heading);
      const details = document.createElement('dl');
      model.records?.forEach((record) => {
        const pair = document.createElement('div');
        const title = document.createElement('dt');
        title.textContent = record.label;
        const value = document.createElement('dd');
        if (record.cell) [...record.cell.childNodes].forEach((node) => value.append(node.cloneNode(true)));
        else value.textContent = record.text;
        pair.append(title, value);
        details.append(pair);
      });
      const appearance = this.querySelector('[data-model-appearance]');
      const appearanceText = appearance?.content.querySelector(`[data-model="${model.code}"]`)?.textContent;
      if (appearanceText) {
        const pair = document.createElement('div');
        const title = document.createElement('dt');
        title.textContent = appearance.dataset.label;
        const value = document.createElement('dd');
        value.textContent = appearanceText;
        pair.append(title, value);
        details.append(pair);
      }
      model.rows.forEach((row) => {
        const headers = row.closest('table').tHead?.rows[0]?.cells;
        [...row.cells].slice(1).forEach((cell, i) => {
          if (cell.querySelector('a') || !cell.textContent.trim()) return;
          const pair = document.createElement('div');
          const title = document.createElement('dt');
          title.textContent = headers?.[i + 1]?.textContent || '';
          const value = document.createElement('dd');
          [...cell.childNodes].forEach((node) => value.append(node.cloneNode(true)));
          pair.append(title, value);
          details.append(pair);
        });
      });
      this.summary.append(details);
      this.models.forEach((entry) => entry.rows.forEach((row) => row.classList.toggle('bp-mg-selected-row', entry === model)));
    }

    restoreFallback() {
      this.ready = false;
      this.abort?.abort();
      this.resize?.disconnect();
      this.pickerObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.summary?.remove();
      this.host?.removeAttribute('data-bp-model-gallery-ready');
      if (this.nativeGallery) this.nativeGallery.hidden = false;
      this.hidden = true;
    }

    disconnectedCallback() {
      this.restoreFallback();
    }
  });
}
